import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { notifyEventAttendanceChange, isBoardEvent } from '../utils/notifications';

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  highlight?: boolean;
  createdAt?: string;
  attendees?: number;
  boardInitiated?: boolean;
  eventStart?: string;
  description?: string;
  category?: string;
}

interface EventStore {
  events: Event[];
  attending: string[];
  declined: string[];
  savedEventIds: string[];
  loading: boolean;
  lastResidenceId?: number;
  lastFetchedAt?: number;
  fetchUpcomingEvents: (residenceId?: number) => Promise<void>;
  fetchAllEvents: (residenceId?: number) => Promise<void>;
  addEvent: (event: Event) => void;
  removeEvent: (id: string) => void;
  isEventAdded: (id: string) => boolean;
  toggleAttendance: (id: string, newStatusOverride?: 'attending' | 'declined', explanation?: string) => void;
  isAttending: (id: string) => boolean;
  isDeclined: (id: string) => boolean;
  getAttendanceStatus: (id: string) => 'attending' | 'declined' | 'not_replied';
  addToUpcoming: (eventId: string) => Promise<void>;
  removeFromUpcoming: (eventId: string) => Promise<void>;
  isInUpcoming: (eventId: string) => boolean;
}

export const useEventStore = create<EventStore>()(
  persist(
    (set, get) => ({
      events: [],
      attending: [],
      declined: [],
      savedEventIds: [],
      loading: false,
      lastResidenceId: undefined,
      lastFetchedAt: undefined,
      
      fetchUpcomingEvents: async (residenceId?: number) => {
        try {
          set({ loading: true });
          
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          // 1) Hent brukerens lagrede upcoming events
          // 2) Hent styreopprettede events for valgt bolig (residenceId), framtidige
          const [upcomingResult, boardResult] = await Promise.all([
            supabase
            .from('upcoming_events')
            .select(`
              event_id,
              base_post!inner (
                id,
                title,
                content,
                board_initiated,
                created_at,
                forum_event!inner (
                  event_start,
                  event_end
                )
              )
            `)
            .eq('user_id', user.id)
              .order('added_at', { ascending: false }),

            residenceId
              ? supabase
                  .from('base_post')
                  .select(`
                    id,
                    title,
                    content,
                    board_initiated,
                    created_at,
                    residence_id,
                    forum_type_id,
                    forum_event!inner (
                      event_start,
                      event_end
                    )
                  `)
                  .eq('board_initiated', true)
                  .eq('forum_type_id', 2)
                  .eq('residence_id', residenceId)
                  .gte('forum_event.event_start', new Date().toISOString())
              : Promise.resolve({ data: [], error: null })
          ]);

          const upcomingEvents = (upcomingResult.data || []) as Array<{
            event_id: number;
            base_post: {
              id: number;
              title: string | null;
              content: string | null;
              board_initiated: boolean | null;
              created_at: string;
              forum_event: { event_start: string; event_end: string | null };
            };
          }>;

          if (upcomingResult.error) {
            console.error('Feil ved henting av upcoming events:', upcomingResult.error);
          }

          const boardEvents = (boardResult as any).data as Array<{
            id: number;
            title: string | null;
            content: string | null;
            board_initiated: boolean | null;
            created_at: string;
            residence_id: number;
            forum_type_id: number;
            forum_event: { event_start: string; event_end: string | null };
          }> || [];
          if ((boardResult as any).error) {
            console.error('Feil ved henting av styre-events:', (boardResult as any).error);
          }

          // Samle alle post-ider fra begge kilder
          const upcomingIds = upcomingEvents.map(ue => ue.event_id);
          const boardIds = boardEvents.map(be => be.id);
          const allIds = Array.from(new Set([...
            upcomingIds,
            ...boardIds
          ]));

          // Hent attendance data for å beregne deltakere
          const { data: attendanceData } = await supabase
            .from('event_attendance')
            .select('post_id, state')
            .in('post_id', allIds)
            .eq('state', 'attending');

          // Grupper attendance data
          const attendanceCounts = attendanceData?.reduce((acc, item) => {
            acc[item.post_id] = (acc[item.post_id] || 0) + 1;
            return acc;
          }, {} as Record<number, number>) || {};

          // Sjekk hvilke events brukeren er påmeldt og avslått
          const [userAttendingRes, userDeclinedRes] = await Promise.all([
            supabase
              .from('event_attendance')
              .select('post_id')
              .eq('user_id', user.id)
              .eq('state', 'attending')
              .in('post_id', allIds.length > 0 ? allIds : []),
            supabase
              .from('event_attendance')
              .select('post_id')
              .eq('user_id', user.id)
              .eq('state', 'declined')
              .in('post_id', allIds.length > 0 ? allIds : [])
          ]);

          const userAttendingIds = userAttendingRes.data?.map(item => item.post_id.toString()) || [];
          const userDeclinedIds = userDeclinedRes.data?.map(item => item.post_id.toString()) || [];

          // Transformer data til Event format og slå sammen begge kilder
          const normalize = (
            id: number,
            title: string | null,
            content: string | null,
            boardInitiated: boolean | null,
            createdAt: string,
            event_start: string,
            event_end: string | null
          ): Event => {
            const eventStartDate = new Date(event_start);
            const eventEndDate = event_end ? new Date(event_end) : null;
            return {
              id: id.toString(),
              title: title || '',
              date: eventStartDate.toISOString().split('T')[0],
              time: eventEndDate
                ? `${eventStartDate.toLocaleTimeString('no', { hour: '2-digit', minute: '2-digit' })} - ${eventEndDate.toLocaleTimeString('no', { hour: '2-digit', minute: '2-digit' })}`
                : eventStartDate.toLocaleTimeString('no', { hour: '2-digit', minute: '2-digit' }),
              highlight: !!boardInitiated,
              createdAt,
              attendees: attendanceCounts[id] || 0,
              boardInitiated: !!boardInitiated,
              eventStart: event_start,
              description: content || undefined
            };
          };

          const upcomingTransformed: Event[] = upcomingEvents.map(ue => normalize(
            ue.event_id,
            ue.base_post.title,
            ue.base_post.content,
            ue.base_post.board_initiated,
            ue.base_post.created_at,
            ue.base_post.forum_event.event_start,
            ue.base_post.forum_event.event_end
          ));

          const boardTransformed: Event[] = boardEvents.map(be => normalize(
            be.id,
            be.title,
            be.content,
            be.board_initiated,
            be.created_at,
            be.forum_event.event_start,
            be.forum_event.event_end
          ));

          // Dedupliser: prioriter brukerens "upcoming" versjon dersom duplikat
          const mergedMap = new Map<string, Event>();
          for (const ev of [...boardTransformed, ...upcomingTransformed]) {
            mergedMap.set(ev.id, ev);
          }
          const mergedEvents = Array.from(mergedMap.values());

          set({ 
            events: mergedEvents,
            attending: userAttendingIds,
            declined: userDeclinedIds,
            savedEventIds: upcomingIds.map(id => id.toString()),
            loading: false
          });
        } catch (error) {
            console.error('Feil ved henting av upcoming events:', error);
          set({ loading: false });
        }
      },

      fetchAllEvents: async (residenceId?: number) => {
        try {
          set({ loading: true });
          
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          if (!residenceId) return;

          // Enkel cache: hopp over hvis samme bolig og nylig hentet (< 60s)
          const { lastResidenceId, lastFetchedAt } = get();
          const nowTs = Date.now();
          if (lastResidenceId === residenceId && lastFetchedAt && nowTs - lastFetchedAt < 60_000) {
            set({ loading: false });
            return;
          }

          // Hent alle events for boligen
          const { data: allEvents, error: eventsError } = await supabase
            .from('base_post')
            .select(`
              id,
              title,
              content,
              board_initiated,
              created_at,
              forum_type_id
            `)
            .eq('residence_id', residenceId)
            .eq('forum_type_id', 2)
            .order('created_at', { ascending: false });

          if (eventsError) {
            console.error('Feil ved henting av alle events:', eventsError);
            return;
          }

          // Hent forum_event data for alle events
          const eventIds = allEvents?.map(event => event.id) || [];
          const [forumEvRes, upcomingRes, attendanceRes, userAttendanceRes] = await Promise.all([
            supabase.from('forum_event').select('id, event_start, event_end').in('id', eventIds),
            supabase.from('upcoming_events').select('event_id').eq('user_id', user.id),
            eventIds.length ? supabase.from('event_attendance').select('post_id, state').in('post_id', eventIds).eq('state', 'attending') : Promise.resolve({ data: [], error: null }),
            eventIds.length ? supabase.from('event_attendance').select('post_id').eq('user_id', user.id).eq('state', 'attending').in('post_id', eventIds) : Promise.resolve({ data: [], error: null })
          ] as const);

          const forumEvents = forumEvRes.data || [];
          if (forumEvRes.error) {
            console.error('Feil ved henting av forum events:', forumEvRes.error);
          }

          // Kombiner data
          const eventsWithForumData = allEvents?.map(event => {
            const forumEvent = forumEvents?.find(fe => fe.id === event.id);
            return {
              ...event,
              forum_event: forumEvent
            };
          }).filter(event => event.forum_event) || []; // Kun events med forum_event data

          // Bruk resultater fra parallelle kall
          const userUpcomingIds = (upcomingRes.data || []).map((ue: any) => ue.event_id.toString());
          const attendanceData = (attendanceRes.data || []) as Array<{ post_id: number; state: string }>;

          // Grupper attendance data
          const attendanceCounts = attendanceData?.reduce((acc, item) => {
            acc[item.post_id] = (acc[item.post_id] || 0) + 1;
            return acc;
          }, {} as Record<number, number>) || {};

          // Sjekk hvilke events brukeren er påmeldt og avslått
          const userAttending = (userAttendanceRes.data || []) as Array<{ post_id: number }>;
          const userAttendingIds = userAttending.map(item => item.post_id.toString());
          
          // Hent også declined events
          const { data: userDeclinedRes } = await supabase
            .from('event_attendance')
            .select('post_id')
            .eq('user_id', user.id)
            .eq('state', 'declined')
            .in('post_id', eventIds);

          const userDeclined = (userDeclinedRes || []) as Array<{ post_id: number }>;
          const userDeclinedIds = userDeclined.map(item => item.post_id.toString());

          // Transformer data til Event format
          const transformedEvents: Event[] = eventsWithForumData
            .filter(ev => !!ev.forum_event)
            .map(event => {
              const fe = event.forum_event!;
              const eventStart = new Date(fe.event_start);
              const eventEnd = fe.event_end ? new Date(fe.event_end) : null;

            return {
                id: event.id.toString(),
                title: event.title || '',
              date: eventStart.toISOString().split('T')[0],
              time: eventEnd 
                ? `${eventStart.toLocaleTimeString('no', { hour: '2-digit', minute: '2-digit' })} - ${eventEnd.toLocaleTimeString('no', { hour: '2-digit', minute: '2-digit' })}`
                : eventStart.toLocaleTimeString('no', { hour: '2-digit', minute: '2-digit' }),
                highlight: userUpcomingIds.includes(event.id.toString()),
                createdAt: event.created_at,
                attendees: attendanceCounts[event.id] || 0,
                boardInitiated: event.board_initiated,
                eventStart: fe.event_start,
                description: event.content || undefined,
                category: 'event' // Default category
            };
          });

          set({ 
            events: transformedEvents,
            attending: userAttendingIds,
            declined: userDeclinedIds,
            savedEventIds: userUpcomingIds,
            loading: false,
            lastResidenceId: residenceId,
            lastFetchedAt: nowTs
          });
        } catch (error) {
          console.error('Feil ved henting av alle events:', error);
          set({ loading: false });
        }
      },

      addEvent: (event) => 
        set((state) => ({
          events: [...state.events, event]
        })),
        
      removeEvent: (id) =>
        set((state) => ({
          events: state.events.filter((event) => event.id !== id)
        })),
        
      isEventAdded: (id) => 
        get().events.some((event) => event.id === id),
        
      toggleAttendance: async (id, newStatusOverride?: 'attending' | 'declined', explanation?: string) => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error('Bruker ikke autentisert');

          const isCurrentlyAttending = get().attending.includes(id);
          const newStatus: 'attending' | 'declined' = newStatusOverride ? newStatusOverride : (isCurrentlyAttending ? 'declined' : 'attending');

          // Sjekk om eventet krever explanation når man avslår
          if (newStatus === 'declined') {
            const { data: eventData, error: eventError } = await supabase
              .from('forum_event')
              .select('explanation')
              .eq('id', parseInt(id))
              .single();

            if (eventError) {
              console.error('Feil ved henting av event data:', eventError);
            } else if (eventData?.explanation && !explanation) {
              throw new Error('Begrunnelse kreves for å avslå dette eventet');
            }
          }

          // Sjekk om det allerede finnes en rad for denne brukeren og eventet
          const { data: existingAttendance, error: checkError } = await supabase
            .from('event_attendance')
            .select('post_id, user_id, state')
            .eq('post_id', parseInt(id))
            .eq('user_id', user.id)
            .single();

          if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
            throw checkError;
          }

          if (existingAttendance) {
            // Oppdater eksisterende rad ved å bruke post_id og user_id som samlet primærnøkkel
            const { error } = await supabase
              .from('event_attendance')
              .update({ 
                state: newStatus,
                explanation: newStatus === 'declined' ? explanation : null
              })
              .eq('post_id', parseInt(id))
              .eq('user_id', user.id);

            if (error) throw error;
          } else {
            // Opprett ny rad
            const { error } = await supabase
              .from('event_attendance')
              .insert({
                post_id: parseInt(id),
                user_id: user.id,
                state: newStatus,
                explanation: newStatus === 'declined' ? explanation : null
              });

            if (error) throw error;
          }

          // Sjekk om dette er et styreevent og opprett notifikasjon
          const boardEventInfo = await isBoardEvent(parseInt(id));
          if (boardEventInfo.isBoard && boardEventInfo.residenceId && boardEventInfo.title) {
            const { data: userProfile } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', user.id)
              .single();

            const userName = userProfile 
              ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim()
              : 'En beboer';

            await notifyEventAttendanceChange(
              parseInt(id),
              boardEventInfo.residenceId,
              userName,
              boardEventInfo.title,
              newStatus as 'attending' | 'declined',
              existingAttendance?.state || undefined,
              explanation
            );
          }

          // Oppdater lokal state umiddelbart (ikke kall fetchUpcomingEvents)
          set((state) => {
            let newAttending = state.attending;
            let newDeclined = state.declined;
            
            if (newStatus === 'attending') {
              // Fjern fra declined hvis det var der, legg til i attending
              newDeclined = newDeclined.filter(eventId => eventId !== id);
              newAttending = newAttending.includes(id) ? newAttending : [...newAttending, id];
            } else if (newStatus === 'declined') {
              // Fjern fra attending hvis det var der, legg til i declined
              newAttending = newAttending.filter(eventId => eventId !== id);
              newDeclined = newDeclined.includes(id) ? newDeclined : [...newDeclined, id];
            }
            
            // Update attendees count in events
            const updatedEvents = state.events.map(event => {
              if (event.id === id) {
                const currentAttendees = event.attendees || 0;
                const newAttendees = newStatus === 'attending' 
                  ? currentAttendees + (isCurrentlyAttending ? 0 : 1)
                  : currentAttendees - (isCurrentlyAttending ? 1 : 0);
                return {
                  ...event,
                  attendees: Math.max(0, newAttendees)
                };
              }
              return event;
            });

            return {
              attending: newAttending,
              declined: newDeclined,
              events: updatedEvents
            };
          });
        } catch (error) {
          console.error('Feil ved toggle av event attendance:', error);
          throw error;
        }
      },
      
      isAttending: (id) =>
        get().attending.includes(id),
        
      isDeclined: (id) =>
        get().declined.includes(id),
        
      getAttendanceStatus: (id) => {
        const { attending, declined } = get();
        if (attending.includes(id)) return 'attending';
        if (declined.includes(id)) return 'declined';
        return 'not_replied';
      },

      addToUpcoming: async (eventId: string) => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error('Bruker ikke autentisert');

          const { error } = await supabase
            .from('upcoming_events')
            .insert({
              event_id: parseInt(eventId),
              user_id: user.id
            });

          if (error) throw error;

          // Oppdater lokal state direkte
          set((state) => ({
            savedEventIds: [...state.savedEventIds, eventId]
          }));
        } catch (error) {
          console.error('Feil ved lagring av event til upcoming:', error);
          throw error;
        }
      },

      removeFromUpcoming: async (eventId: string) => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error('Bruker ikke autentisert');

          const { error } = await supabase
            .from('upcoming_events')
            .delete()
            .eq('event_id', parseInt(eventId))
            .eq('user_id', user.id);

          if (error) throw error;

          // Oppdater lokal state direkte
          set((state) => ({
            savedEventIds: state.savedEventIds.filter(id => id !== eventId)
          }));
        } catch (error) {
          console.error('Feil ved sletting av event fra upcoming:', error);
          throw error;
        }
      },

      isInUpcoming: (eventId: string) =>
        get().savedEventIds.includes(eventId)
    }),
    {
      name: 'event-storage',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      migrate: (persistedState: any, version: number) => {
        if (version === 0 || version === 1) {
          // Migration from version 0/1 to 2 - reset to empty state
          return {
            events: [],
            attending: [],
            declined: [],
            loading: false
          };
        }
        return persistedState;
      }
    }
  )
);